import { App, type AppState } from '@capacitor/app'
import { connectWebSocket, disconnectWebSocket, sendSubscription } from '../services/websocket'
import { WS_URL } from '../services/env'
import { useRiderStore } from '../store/rider'
import { useTransitStore } from '../store/transit'

export function useWebSocket() {
  // Plain local, not reactive state: nothing renders from it, it only remembers whether
  // the last appStateChange put us in the background.
  let appActive: AppState['isActive'] = true

  $effect(() => {
    connectWebSocket(WS_URL)

    // Mirror what the map draws (focused routes, else the mode filter, plus the boarded
    // vehicle's line) to the backend so each client only receives those vehicles — see
    // clientSubscription.
    const pushSubscription = () => sendSubscription()

    const unsubRoute = useTransitStore.subscribe((state, prev) => {
      if (state.selectedRouteIds === prev.selectedRouteIds &&
          state.tripRouteIds === prev.tripRouteIds &&
          state.transitFilter === prev.transitFilter) return
      pushSubscription()
    })

    // Boarding changes what this client needs pushed to it: the rider's own vehicle must
    // keep updating even when its line sits outside the current focus.
    const unsubRider = useRiderStore.subscribe((state, prev) => {
      if (state.trackedVehicleId === prev.trackedVehicleId) return
      pushSubscription()
    })

    const listenerPromise = App.addListener('appStateChange', (state: AppState) => {
      const wasBackground = !appActive
      appActive = state.isActive

      if (wasBackground && state.isActive) {
        const { isRiding } = useRiderStore.getState()
        if (!isRiding) {
          connectWebSocket(WS_URL)
        }
      }
    })

    return () => {
      unsubRoute()
      unsubRider()
      listenerPromise.then((listener) => listener.remove())
      disconnectWebSocket()
    }
  })
}
